export type BlogSection = {
  h2: string;
  p?: string[];
  bullets?: string[];
  checklist?: string[];
  note?: string;
};

export type BlogPost = {
  id: string;
  title: string;
  kicker: string;
  excerpt: string;
  subtitle?: string;
  tags: string[];
  author: string;
  date: string;
  read?: number;
  sections?: BlogSection[];
  body?: string;
};

export const BLOG_POSTS: BlogPost[] = [
  // ReferralCX
  {
    id: "referrals-unified-platform",
    title: "The Future of Referrals: Why Internal & External Referrals Need a Unified Platform",
    kicker: "ReferralCX",
    excerpt:
      "Fragmented referral systems increase leakage, delays, and denials. Here’s how a unified approach improves access, throughput, and outcomes.",
    subtitle:
      "Fragmented referral systems increase leakage, delays, and denials. Here’s how a unified approach improves access, throughput, and outcomes.",
    tags: ["ReferralCX"],
    author: "Dawniela Hightower",
    date: "2025-09-10",
    read: 6,
    sections: [
      {
        h2: "Why referrals break today",
        p: [
          "Most organizations juggle internal specialty referrals, external community referrals, and self-referrals across disconnected tools. This creates opaque handoffs, inconsistent triage, and delayed booking—ultimately driving leakage and denials.",
          "When referral requests arrive via fax, email, or portals with different intake fields, staff spend time normalizing data instead of helping patients. Leaders get volume without visibility and can’t forecast demand or capacity.",
        ],
        bullets: [
          "Duplicate data entry and swivel‑chair workflows",
          "Eligibility/authorization checks performed late (or not at all)",
          "Routing rules live in people’s heads instead of the platform",
          "No shared status signal across facilities and partners",
        ],
      },
      {
        h2: "A unified referral model",
        p: [
          "A modern platform should bring **all** referral types into a single pipeline with configurable steps. In Eceptionist’s ReferralCX, each referral moves through standardized states with role‑based tasks and SLAs—so every team sees the same source of truth.",
        ],
        bullets: [
          "Digital intake with templates and validation",
          "Automated triage and program/service matching",
          "Capacity‑aware scheduling and patient communications",
          "Complete audit trail and analytics",
        ],
      },
      {
        h2: "What this changes for ops",
        p: [
          "Teams shift from chasing paperwork to managing outcomes. With shared worklists and queue health, leaders can balance caseloads, reduce backlog, and accelerate time‑to‑book.",
        ],
        bullets: [
          "\u003cstrong\u003e30–50%\u003c/strong\u003e less manual rework via templates and rules",
          "\u003cstrong\u003e15–25%\u003c/strong\u003e faster time‑to‑scheduled thanks to guided triage",
          "Fewer denials by catching eligibility issues up front",
        ],
        note: "Benchmarks vary by program size and payer mix; your results may differ.",
      },
      {
        h2: "Design principles we follow",
        p: [
          "We build for real‑world complexity: multiple facilities, programs, and external partners. Our approach emphasizes governance without sacrificing speed.",
        ],
        bullets: [
          "\u003cstrong\u003eSingle pipeline, multiple views:\u003c/strong\u003e coordinators, schedulers, and leaders each see what they need—on the same data.",
          "\u003cstrong\u003eConfig over custom:\u003c/strong\u003e admin UI for forms, routing, SLAs, and notifications.",
          "\u003cstrong\u003eHuman‑in‑the‑loop AI:\u003c/strong\u003e suggestions for routing/urgency with transparent audit trails.",
        ],
      },
      {
        h2: "Getting started",
        p: [
          "Most teams pilot one or two services first—then scale. We recommend mapping current states, defining target SLAs, and capturing a minimum service catalog with intake rules.",
        ],
        checklist: [
          "Pick 2–3 high‑volume services",
          "Document intake fields and eligibility rules",
          "Define routing logic (site, provider, payer, language, risk)",
          "Stand up referral worklists and dashboards",
          "Train coordinators and schedulers on the new workflow",
        ],
      },
    ],
  },
  // AI
  {
    id: "ai-in-cx",
    title: "AI in EceptionistCX: Beyond the Hype, Toward Practical Impact",
    kicker: "AICX",
    excerpt:
      "Where AI actually pays off: triage, routing, eligibility validation, and next‑best actions—not just chatbots.",
    subtitle:
      "Where AI actually pays off: capturing referrals, triage, routing, and resource utilization embedded into workflows.",
    tags: ["AI"],
    author: "Dirk Voorhees",
    date: "2025-09-09",
    read: 6,
    sections: [
      { h2: "Why AI in operations", p: [
        "Healthcare systems often look to technology to reduce friction, but the biggest efficiency gains come from streamlining referral capture, routing, triage, and task prioritization.",
        "For example, using AI to read faxed referrals and turn them into structured electronic referrals eliminates hours of manual data entry while reducing errors. Coordinators can start work with complete, discrete data from day one.",
      ], bullets: [
        "AI‑scored urgency for incoming referrals",
        "Automated capture of faxed referrals into electronic forms",
        "Suggested routing based on service catalogs and program rules",
        "Next‑best‑action prompts for coordinators",
      ]},
      { h2: "Our approach to AI in EceptionistCX", p: [
        "We focus on \u003cstrong\u003ehuman‑in‑the‑loop\u003c/strong\u003e models. The system makes recommendations—staff make the final calls. This keeps governance intact while reducing repetitive checks.",
        "AI also helps manage capacity by surfacing where bottlenecks are forming—beds, infusion chairs, clinic appointments—and proactively suggesting adjustments to scheduling or resource allocation.",
      ], bullets: [
        "Transparent logic with audit trails",
        "Configurable confidence thresholds",
        "Dashboards showing impact on wait times, resource utilization, and rework",
      ]},
      { h2: "What customers see in practice", p: [
        "Early adopters of AI in CX modules report reductions in both staff burden and patient delays. By capturing referrals electronically and surfacing next‑steps instantly, staff shift focus from data entry to patient engagement. Capacity management features ensure resources are aligned with demand.",
      ], bullets: [
        "\u003cstrong\u003e20–30%\u003c/strong\u003e fewer manual escalations and transcription errors",
        "\u003cstrong\u003e10–15%\u003c/strong\u003e faster referral cycle times",
        "Improved alignment of staff, beds, and appointment slots with demand",
      ]},
      { h2: "Design principles we follow", p: [
        "AI is only as useful as its adoption. That means we design lightweight UI hints instead of heavy, disruptive pop‑ups.",
      ], bullets: [
        "Prompts embedded directly in worklists",
        "Explainability: staff can see why AI made a recommendation",
        "Continuous learning with admin‑tunable rules",
      ]},
      { h2: "How to start with AI in CX", p: [
        "We recommend piloting AI on one or two narrow use cases—like referral fax capture or urgency scoring—before scaling across departments.",
      ], checklist: [
        "Identify 1–2 high‑volume workflows with delays or manual data entry",
        "Define measurable metrics (time to schedule, cycle times, resource utilization)",
        "Enable AI in CX prompts in coordinator worklists",
        "Review results after 60 days and expand scope",
      ]},
    ],
  },
  // CellCX
  {
    id: "cellcx-from-lab-to-bedside",
    title: "From Lab to Bedside: Orchestrating Complex Cell Therapy Workflows with CellCX",
    kicker: "CellCX",
    excerpt:
      "CAR‑T requires precise, multi-party coordination. CellCX helps harmonize eligibility, apheresis, manufacturing, logistics, and infusion scheduling.",
    subtitle:
      "How purpose‑built workflow tools accelerate CAR‑T and other advanced therapies from collection to infusion.",
    tags: ["CellCX"],
    author: "Dawniela Hightower",
    date: "2025-09-07",
    read: 8,
    sections: [
      { h2: "Why cell therapy needs purpose‑built workflows", p: [
        "Cell therapy is unlike traditional infusions. Each patient is their own batch, with handoffs across apheresis, manufacturing, labs, logistics, and clinical teams.",
        "Generic scheduling tools break down when you need milestone‑based orchestration across multiple teams. That’s where CellCX comes in.",
      ], bullets: [
        "Manage patient journey from referral to infusion",
        "Coordinate labs, couriers, and manufacturing slots",
        "Surface bottlenecks early to avoid delays",
      ]},
      { h2: "How CellCX works", p: [
        "CellCX provides a configurable backbone for advanced therapies. Each case follows a state machine with tasks, SLAs, and notifications.",
        "Teams see the same single source of truth while still having role‑based dashboards for coordinators, lab staff, and investigators.",
      ], bullets: [
        "Digital intake with therapy‑specific templates",
        "Milestone tracking (collection, manufacturing, release, infusion)",
        "Alerts for deviations or delays",
        "Analytics on turnaround times and slot utilization",
      ]},
      { h2: "What this changes for programs", p: [
        "Programs using CellCX report fewer missed milestones, smoother cross‑team handoffs, and more predictable infusion dates.",
      ], bullets: [
        "\u003cstrong\u003e20–40%\u003c/strong\u003e fewer last‑minute reschedules",
        "Better utilization of scarce manufacturing slots",
        "Reduced coordinator burnout from manual spreadsheets",
      ]},
      { h2: "Design principles we follow", p: [
        "Cell therapy demands rigor without sacrificing speed. We designed CellCX with both compliance and usability in mind.",
      ], bullets: [
        "\u003cstrong\u003eTransparency first:\u003c/strong\u003e status visible across sites and vendors.",
        "\u003cstrong\u003eNo black boxes:\u003c/strong\u003e progress is always visible and auditable.",
        "\u003cstrong\u003eConfig over custom:\u003c/strong\u003e adapt templates and rules without coding.",
      ]},
      { h2: "Getting started with CellCX", p: [
        "Most centers start with CAR‑T and expand to other therapies. Success depends on mapping your milestones, defining SLA expectations, and engaging both clinical and lab teams early.",
      ], checklist: [
        "Define therapy types and milestone states",
        "Configure intake forms and workflow rules",
        "Set alerts for lab/manufacturing turnaround times",
        "Pilot with 5–10 patients before full rollout",
      ]},
    ],
  },
  // ProbationCX
  {
    id: "probationcx-digital-referrals",
    title: "Modernizing Probation: How ProbationCX Digitizes Referrals and Case Management",
    kicker: "ProbationCX",
    excerpt:
      "Paper-based referrals and manual status checks slow outcomes. See how ProbationCX improves visibility, compliance, and caseload balance.",
    subtitle:
      "Moving from paper and spreadsheets to automated referrals, digital workflows, and real‑time reporting.",
    tags: ["ProbationCX"],
    author: "Dawniela Hightower",
    date: "2025-09-06",
    read: 7,
    sections: [
      { h2: "Why probation services need modernization", p: [
        "Probation departments handle thousands of referrals every year—from courts, treatment providers, and community programs.",
        "Paper‑based intake and siloed spreadsheets create delays, errors, and compliance risks. Staff spend more time chasing paperwork than focusing on client success.",
      ], bullets: [
        "Referrals arrive by paper, fax, or email with no consistent format",
        "Manual data entry increases errors and workload",
        "Little visibility for supervisors or stakeholders",
      ]},
      { h2: "How ProbationCX works", p: [
        "ProbationCX brings all referrals and case data into a unified digital platform. Every referral moves through configurable steps with tasks, alerts, and SLAs.",
        "Supervisors gain real‑time dashboards, while officers access clear worklists and communication tools.",
      ], bullets: [
        "Digital referral intake with validation rules",
        "Automated routing to officers or programs",
        "Shared dashboards with status and outcomes",
        "Alerts for overdue tasks or missed milestones",
      ]},
      { h2: "What this changes for departments", p: [
        "Agencies using ProbationCX reduce delays, improve compliance, and free up staff capacity.",
      ], bullets: [
        "\u003cstrong\u003e30–40%\u003c/strong\u003e reduction in manual data entry",
        "Improved visibility into caseload distribution",
        "Better collaboration with courts and providers",
      ]},
      { h2: "Design principles we follow", p: [
        "Probation systems must balance accountability, compliance, and rehabilitation. ProbationCX was designed with these values in mind.",
      ], bullets: [
        "\u003cstrong\u003eConfigurable forms and workflows:\u003c/strong\u003e adapt to each jurisdiction’s requirements.",
        "\u003cstrong\u003eTransparency first:\u003c/strong\u003e officers, supervisors, and courts see the same data.",
        "\u003cstrong\u003eScalable architecture:\u003c/strong\u003e support for small counties and large state systems.",
      ]},
      { h2: "Getting started with ProbationCX", p: [
        "Most departments begin by digitizing their referral intake process, then expand into broader case management.",
      ], checklist: [
        "Map current referral sources and formats",
        "Define routing logic (officer assignment, program type)",
        "Configure dashboards for supervisors",
        "Pilot with a small caseload before scaling",
      ]},
    ],
  },
  // VaxManagerCX
  {
    id: "vaxmanagercx-public-health-readiness",
    title: "Public Health Readiness: What COVID Taught Us About Vaccine & Clinic Management",
    kicker: "VaxManagerCX",
    excerpt:
      "VaxManagerCX supports mass clinics, employer programs, and ongoing immunization operations with inventory, eligibility, and scheduling tools.",
    subtitle:
      "How VaxManagerCX supports mass clinics, employer programs, and ongoing immunization operations.",
    tags: ["VaxManagerCX"],
    author: "Trey Havlick",
    date: "2025-09-03",
    read: 5,
    sections: [
      { h2: "Why vaccine management needs modernization", p: [
        "COVID‑19 highlighted how unprepared many systems were for mass vaccination. Scheduling backlogs, long lines, and data silos slowed public health response.",
        "Ongoing readiness means treating vaccine and clinic management as a core capability, not a one‑time event.",
      ], bullets: [
        "Long wait times due to manual scheduling",
        "Limited reporting for state and federal agencies",
        "Difficulty handling large wait lists or eligibility groups",
      ]},
      { h2: "How VaxManagerCX works", p: [
        "VaxManagerCX provides a unified platform to manage clinics, patient scheduling, and communications.",
        "It supports high‑volume mass vaccination events as well as routine employer and community programs.",
      ], bullets: [
        "Online scheduling with eligibility checks",
        "Wait list management when demand exceeds available appointments",
        "Digital consent forms and pre‑visit questionnaires",
        "Real‑time dashboards for doses administered",
        "Integration with immunization registries",
      ]},
      { h2: "What this changes for programs", p: [
        "Agencies using VaxManagerCX report smoother clinics, higher throughput, and improved reporting accuracy.",
      ], bullets: [
        "\u003cstrong\u003e25–35%\u003c/strong\u003e faster appointment throughput",
        "Improved compliance with public health reporting requirements",
        "Better patient experience through streamlined forms, consents, and wait list handling",
      ]},
      { h2: "Design principles we follow", p: [
        "Public health systems must be resilient, adaptable, and transparent. VaxManagerCX was built with these principles in mind.",
      ], bullets: [
        "\u003cstrong\u003eScalable architecture:\u003c/strong\u003e support for both mass clinics and small programs.",
        "\u003cstrong\u003eConfigurable workflows:\u003c/strong\u003e adapt eligibility, reminders, and reporting rules.",
        "\u003cstrong\u003eTransparent dashboards:\u003c/strong\u003e real‑time data for staff and leadership.",
      ]},
      { h2: "Getting started with VaxManagerCX", p: [
        "Most organizations begin with scheduling and patient intake, then expand to communications and reporting.",
      ], checklist: [
        "Set up scheduling rules for clinics and programs",
        "Integrate with state immunization registry",
        "Configure digital consent forms and questionnaires",
        "Pilot with one employer or community program before scaling",
      ]},
    ],
  },
];


